import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { DateTimeWidget, ImageWidget, LanguageDropdownWidget, SelectWidget } from "./CustomWidgets"
import {
  collection,
  getDocs,
  limit,
  query,
  startAfter,
  where,
  Query,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore"
import { db } from "../firebase"
import CustomObjectFieldTabs from "./raffle/CustomObjectFieldTabs"
import { locationUISchema } from "react-ui-components/components/location/locationUISchema"
import LocationSelector from "react-ui-components/components/location/LocationSelector"
import type { WidgetProps } from "@rjsf/utils"
import { UrlWidget } from "./UrlWidget"
import { BusinessTypes, FeaturedFormData, Business, Service } from "./FeaturedFormData"
import { StoresMultiSelectWidget } from "./StoresMultiSelectWidget"
import { DataProvider, ListComponent } from "../GenericList"
import { getFeaturedBusinessesCollectionPath } from "./featuredBusinessesPaths"

const TYPE_HINTS: Record<BusinessTypes, string> = {
  Business: "External entity that links outside the page.",
  Stores:
    "Internal business group. Select which WinFacil businesses to associate with this one.",
  Service: "Link to an internal WinFacil service from an existing business.",
  Sponsor: "Link to an internal WinFacil sponsor page.",
}

const TypeSelectWidget: React.FC<WidgetProps> = (props) => {
  const { formContext, registry } = props
  const formData = (formContext?.formData ?? registry?.formContext?.formData ?? {}) as FeaturedFormData
  const hint = TYPE_HINTS[formData?.type as BusinessTypes] ?? ""
  const sponsorValue = formData?.sponsorId ?? ""

  useEffect(() => {
    if (formData?.type !== "Sponsor" && sponsorValue) {
      formContext?.updateFormData?.({ sponsorId: "" })
    }
  }, [formContext, formData?.type, sponsorValue])

  const sponsorWidgetProps = {
    value: sponsorValue,
    onChange: (next: string) => formContext?.updateFormData?.({ sponsorId: next }),
    formContext,
    registry,
    disabled: false,
  } as unknown as WidgetProps

  return (
    <div>
      <SelectWidget {...props} />
      {!hint ? null : (
        <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>{hint}</div>
      )}
      {formData?.type === "Sponsor" ? (
        <div style={{ marginTop: 8 }}>
          <SponsorSelectWidget {...sponsorWidgetProps} />
        </div>
      ) : null}
    </div>
  )
}

export const ExternalUrlWidget: React.FC<WidgetProps> = (props) => {
  const { formContext, registry, onChange, value } = props
  const parentFormContext = (formContext?.formData ?? registry?.formContext?.formData ?? {}) as FeaturedFormData
  const isBusiness = parentFormContext?.type === "Business"

  useEffect(() => {
    if (!isBusiness && value) {
      onChange?.("")
    }
  }, [isBusiness, onChange, value])

  return (
    <div hidden={!isBusiness}>
      <UrlWidget {...props} />
    </div>
  )
}

// -------------------- BusinessSelectWidget --------------------
export const BusinessSelectWidget: React.FC<WidgetProps> = (props) => {
  const { value, onChange, formContext } = props
  const formData = (formContext?.formData ?? {}) as FeaturedFormData
  const currentType = formData.type

  const [items, setItems] = useState<Business[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  useEffect(() => {
    console.log("formData--props--->", props)
  }, [props])

  /**
   * Loads business data from Firebase with optional search term
   * @param term - Search term to filter businesses by name
   */
  const load = useCallback(async (term: string) => {
    setLoading(true)
    setError(null)

    try {
      const baseRef = collection(db, "sponsors") // <- tu colección de negocios
      const q = term
        ? query(baseRef, where("name", ">=", term), where("name", "<=", term + "\uf8ff"), limit(50))
        : query(baseRef, limit(50))

      const snap = await getDocs(q)
      const rows: Business[] = snap.docs
        .map((d) => ({ id: d.id, name: String((d.data() as any)?.name ?? d.id) }))
        .sort((a, b) => a.name.localeCompare(b.name))
      setItems(rows)
    } catch (err) {
      console.error("Error loading businesses:", err)
      setError("Failed to load businesses")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load("")
  }, [load])

  const selected = items.find((x) => x.id === value) ?? null

  if (currentType !== "Business" && currentType !== "Service") return null

  return (
    <Autocomplete<Business>
      options={items}
      value={selected}
      onChange={(_, v) => {
        if ((formData?.serviceId ?? "") && v?.id !== value) {
          onChange(v?.id ?? "")
          formContext?.updateFormData?.({ serviceId: "" })
        } else {
          onChange(v?.id ?? "")
        }
      }}
      inputValue={search}
      onInputChange={(_, v) => {
        setSearch(v)
        load(v).catch(() => {
          setLoading(false)
          setError("Failed to search businesses")
        })
      }}
      getOptionLabel={(o) => o.name}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Business"
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export const SponsorSelectWidget: React.FC<WidgetProps> = (props) => {
  const { value, onChange, formContext, disabled } = props
  const formData = (formContext?.formData ?? {}) as FeaturedFormData
  const currentType = formData.type

  const [items, setItems] = useState<Service[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  /**
   * Loads sponsor data from Firebase with optional search term
   * @param term - Search term to filter sponsors by name
   */
  const load = useCallback(async (term: string) => {
    setLoading(true)
    setError(null)

    try {
      const baseRef = collection(db, "sponsors")
      const q = term
        ? query(baseRef, where("name", ">=", term), where("name", "<=", term + "\uf8ff"), limit(50))
        : query(baseRef, limit(50))

      const snap = await getDocs(q)
      const rows: any[] = snap.docs
        .map((d) => ({ id: d.id, name: String((d.data() as any)?.name ?? d.id) }))
        .sort((a, b) => a.name.localeCompare(b.name))
      setItems(rows)
    } catch (err) {
      console.error("Error loading sponsors:", err)
      setError("Failed to load sponsors")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(search).catch(() => {
      setLoading(false)
      setError("Failed to load sponsors")
    })
    if (currentType !== "Sponsor" && value) onChange("")
  }, [currentType])

  const selected = items.find((x) => x.id === value) ?? null
  console.log("currentType--->", currentType)
  if (currentType !== "Sponsor") return null

  return (
    <Autocomplete<any>
      options={items}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? "")}
      inputValue={search}
      onInputChange={(_, v) => {
        setSearch(v)
        load(v).catch(() => {
          setLoading(false)
          setError("Failed to search sponsors")
        })
      }}
      getOptionLabel={(o) => o.name}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={"Select Sponsor"}
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

// -------------------- ServiceSelectWidget --------------------
export const ServiceSelectWidget: React.FC<WidgetProps> = (props) => {
  const { value, onChange, formContext, disabled } = props
  const formData = (formContext?.formData ?? {}) as FeaturedFormData
  const currentType = formData.type
  const businessId = formData.businessId

  const [items, setItems] = useState<Service[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  /**
   * Loads services for a specific business from Firebase
   * @param term - Search term to filter services by title
   */
  const load = useCallback(
    async (term: string) => {
      if (!businessId) {
        setItems([])
        return
      }
      setLoading(true)
      setError(null)

      try {
        const baseRef = collection(db, "sponsors", businessId, "services")
        const q = term
          ? query(
              baseRef,
              where("title", ">=", term),
              where("title", "<=", term + "\uf8ff"),
              limit(50),
            )
          : query(baseRef, limit(50))

        const snap = await getDocs(q)
        const rows: Service[] = snap.docs
          .map((d) => ({ id: d.id, title: String((d.data() as any)?.title ?? d.id) }))
          .sort((a, b) => a.title.localeCompare(b.title))
        setItems(rows)
      } catch (err) {
        console.error("Error loading services:", err)
        setError("Failed to load services")
        setItems([])
      } finally {
        setLoading(false)
      }
    },
    [businessId],
  )

  useEffect(() => {
    load(search).catch(() => {
      setLoading(false)
      setError("Failed to load services")
    })
    if (!businessId && value) onChange("")
  }, [businessId])

  const selected = items.find((x) => x.id === value) ?? null

  if (currentType !== "Service") return null

  return (
    <Autocomplete<Service>
      options={items}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? "")}
      inputValue={search}
      onInputChange={(_, v) => {
        setSearch(v)
        load(v).catch(() => {
          setLoading(false)
          setError("Failed to search services")
        })
      }}
      getOptionLabel={(o) => o.title}
      loading={loading}
      disabled={disabled || !businessId}
      renderInput={(params) => (
        <TextField
          {...params}
          label={businessId ? "Select Service" : "Select a sponsor first"}
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

/** =========================
 *  Types
 *  ========================= */
type User = {
  id: string
  name?: string
}

type AutocompleteWidgetProps = {
  value?: string // userId
  onChange: (value: string) => void
  id?: string
}

/** =========================
 *  Autocomplete Widget (Users)
 *  ========================= */
const AutocompleteWidget: React.FC<AutocompleteWidgetProps> = ({ value = "", onChange }) => {
  const [users, setUsers] = useState<User[]>([])
  const [userSearch, setUserSearch] = useState<string>("")
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [lastVisibleUser, setLastVisibleUser] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null)

  const usersRef = useMemo(() => collection(db, "users"), [])

  /**
   * Fetches users from Firebase with optional search term and pagination
   * @param searchTerm - Term to search users by name
   * @param startAfterDoc - Document to start after for pagination
   */
  const fetchUsers = useCallback(
    async (searchTerm = "", startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null) => {
      setLoadingUsers(true)
      setError(null)

      try {
        let userQuery: Query<DocumentData> = query(
          usersRef,
          where("name", ">=", searchTerm || ""),
          where("name", "<=", (searchTerm || "") + "\uf8ff"),
          limit(100),
        )

        if (startAfterDoc) {
          userQuery = query(userQuery, startAfter(startAfterDoc))
        }

        const querySnapshot = await getDocs(userQuery)
        const docs = querySnapshot.docs

        const lastVisible = docs.length > 0 ? docs[docs.length - 1] : null
        setLastVisibleUser(lastVisible)

        const list: User[] = docs
          .map((d) => {
            const data = d.data() as { name?: string }
            return { id: d.id, name: data?.name }
          })
          .filter((u) => !!u.name)

        setUsers(list)
      } catch (err) {
        console.error("Error fetching users:", err)
        setError("Failed to load users")
        setUsers([])
      } finally {
        setLoadingUsers(false)
      }
    },
    [usersRef],
  )

  useEffect(() => {
    // Initial load of users
    fetchUsers().catch(() => {
      setLoadingUsers(false)
      setError("Failed to load users")
    })
  }, [fetchUsers])

  const selectedUser = useMemo(() => users.find((u) => u.id === value) ?? null, [users, value])

  const handleUserSearchChange = useCallback(
    (_event: unknown, newInputValue: string) => {
      setUserSearch(newInputValue)
      fetchUsers(newInputValue).catch(() => {
        setLoadingUsers(false)
        setError("Failed to search users")
      })
    },
    [fetchUsers],
  )

  return (
    <Autocomplete<User>
      options={users}
      value={selectedUser}
      getOptionLabel={(option) => option.name ?? option.id ?? "Unknown User"}
      onChange={(_evt, newValue) => {
        onChange(newValue ? newValue.id : "")
      }}
      inputValue={userSearch}
      onInputChange={handleUserSearchChange}
      loading={loadingUsers}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name ?? option.id ?? "Unknown User"}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select User"
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

const renderTruncatedText = (value: unknown, maxChars = 60, maxLines = 2) => {
  if (value === null || value === undefined || value === "") return ""
  const text = String(value)
  const display = text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text
  return (
    <span
      title={text}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
        maxWidth: 280,
      }}
    >
      {display}
    </span>
  )
}

const renderArraySummary = (value: unknown, label: string) => {
  if (!Array.isArray(value) || value.length === 0) return ""
  const text = value.map((item) => String(item)).join(", ")
  const count = value.length
  const display = count === 1 ? `1 ${label}` : `${count} ${label}s`
  return (
    <span
      title={text}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
        maxWidth: 280,
      }}
    >
      {display}
    </span>
  )
}

const formatLocationSummary = (value: Record<string, unknown> | null | undefined) => {
  if (!value) return ""
  const { street, city, province, county, zipCode, country } = value as Record<string, unknown>
  return [street, city, province, county, zipCode, country]
    .filter((item) => !!item)
    .map((item) => String(item))
    .join(", ")
}

const renderDateTime = (value: unknown) => {
  if (!value) return ""
  const date = typeof value === "number" ? new Date(value) : new Date(String(value))
  if (Number.isNaN(date.getTime())) return renderTruncatedText(value, 40, 1)
  return renderTruncatedText(date.toLocaleString(), 40, 1)
}

/** =========================
 *  Data Provider Config
 *  ========================= */
export const featureBusinessConfigData = {
  // Navigation and UI configuration
  getListLocation(_id?: string) {
    return getFeaturedBusinessesCollectionPath()
  },
  EDIT_TITLE: "Edit Featured Business",
  ADD_TITLE: "Add Featured Business",
  LOADING_MESSAGE: "Loading featured-businesses...", // Displayed during data fetching
  TITLE: "Featured Business",
  IMAGES_BUCKET: "featuredImages",

  onRowClick:
    (
      list: Array<Record<string, unknown>>,
      navigate: (path: string, opts: { state: unknown }) => void,
    ) =>
    (_row: unknown, index: { dataIndex: number }) => {
      const state = list[index.dataIndex]
      const id = (state as { id?: string })?.id
      if (id) {
        navigate(`/featured-businesses/${id}`, { state })
      }
    },

  columnsProperties: [
    { hidden: true, name: "id", label: "Id" },
    { required: false, name: "disabled", default: false, label: "Disabled", type: "boolean" },
    {
      required: false,
      type: "integer",
      name: "expirationDate",
      label: "Expiration Date",
      options: {
        customBodyRender: (value?: unknown) => renderDateTime(value),
        setCellProps: () => ({ style: { maxWidth: 180 } }),
      },
    },

    { required: true, name: "name", label: "Name" },
    { required: true, type: "integer", name: "order", label: "Order" },
    { required: true, name: "width", label: "Width", default: "100%" },
    { required: true, name: "height", label: "Height", default: "100%" },
    {
      required: true,
      type: "string",
      name: "type",
      label: "Type",
      otherProps: {
        oneOf: [
          { const: "Business", title: "Business" },
          { const: "Service", title: "Service" },
          { const: "Stores", title: "Stores" },
          { const: "Sponsor", title: "Sponsor" },
        ],
      },
      // enum: ["Stores", "Business", "Service", "Sponsor"] as const,
    },
    {
      required: false,
      name: "externalBusinessUrl",
      label: "External URL",
      type: "string",
      options: {
        customBodyRender: (value?: unknown) => renderTruncatedText(value, 50, 1),
        setCellProps: () => ({ style: { maxWidth: 240 } }),
      },
    },
    {
      required: false,
      name: "businessId",
      label: "Business",
      type: "string",
      options: {
        customBodyRender: (value?: unknown) => renderTruncatedText(value, 40, 1),
        setCellProps: () => ({ style: { maxWidth: 200 } }),
      },
    },

    {
      required: false,
      name: "serviceId",
      label: "Service",
      type: "string",
      options: {
        customBodyRender: (value?: unknown) => renderTruncatedText(value, 40, 1),
        setCellProps: () => ({ style: { maxWidth: 200 } }),
      },
    },
    {
      required: false,
      name: "businessIds",
      label: "Stores",
      type: "array",
      items: { type: "string" },
      options: {
        customBodyRender: (value?: unknown) => renderArraySummary(value, "store"),
        setCellProps: () => ({ style: { maxWidth: 200 } }),
      },
    },
    {
      required: false,
      name: "sponsorId",
      label: "Sponsor",
      type: "string",
      options: {
        customBodyRender: (value?: unknown) => renderTruncatedText(value, 40, 1),
        setCellProps: () => ({ style: { maxWidth: 200 } }),
      },
    },
    {
      required: false,
      name: "showAsPopup",
      label: "Show As Popup",
      type: "boolean",
      default: false,
    },
    {
      required: false,
      name: "hideHeader",
      label: "Hide Header",
      type: "boolean",
      default: false,
    },
    {
      type: "string",
      name: "iconUrl",
      label: "Icon URL",
      options: {
        customBodyRender: (value?: string) => (
          <div className="flex flex-col flex-wrap overflow-auto w-40">
            {!value ? null : (
              // Alt accesible con fallback
              <img src={value} alt="Business icon" className="max-w-full h-auto rounded" />
            )}
          </div>
        ),
      },
    },
    {
      required: false,
      name: "description",
      label: "Description",
      options: {
        customBodyRender: (value?: unknown) => renderTruncatedText(value, 90, 2),
        setCellProps: () => ({ style: { maxWidth: 320 } }),
      },
    },
    { required: true, name: "language", label: "Language" },
    // {
    //   required: false,
    //   name: "phoneMessage",
    //   label: "Phone Message",
    //   // ...getMultiLangStringFor_schema_obj("Phone Message"),
    //   // type: "ob",
    // },
    {
      required: false,
      name: "location",
      label: "Location",
      type: "object",
      default: {
        hidden: false,
        country: "",
        province: "",
        city: "",
        street: "",
        county: "",
        zipCode: "",
      },
      properties: {
        country: { type: "string", title: "Country" },
        province: { type: "string", title: "State" },
        city: { type: "string", title: "City" },
        street: { type: "string", title: "Street" },
        county: { type: "string", title: "County" },
        zipCode: { type: "string", title: "Zip Code" },
      },
      options: {
        customBodyRender: (value?: Record<string, unknown> | null) =>
          renderTruncatedText(formatLocationSummary(value ?? undefined), 80, 2),
        setCellProps: () => ({ style: { maxWidth: 260 } }),
      },
    },
    {
      required: false,
      name: "userIds",
      label: "Users",
      type: "array",
      items: { type: "string" },
      options: {
        customBodyRender: (value?: string[]) => renderArraySummary(value, "user"),
        setCellProps: () => ({ style: { maxWidth: 220 } }),
      },
    },
  ],

  uiSchema: {
    // ...getMultiLangStringFor_uiSchema("phoneMessage"),
    // "ui:order": ["type", "externalBusinessUrl", "businessId", "serviceId", "businessIds"],
    type: { "ui:widget": "typeSelect" },
    businessIds: { "ui:widget": "storesMultiSelect" },
    businessId: { "ui:widget": "businessSelect" },
    serviceId: { "ui:widget": "serviceSelect" },
    sponsorId: { "ui:widget": "hidden" },
    externalBusinessUrl: { "ui:widget": "externalBusinessUrl" },

    iconUrl: { "ui:widget": "image" },
    image: { "ui:widget": "image" },
    language: { "ui:widget": LanguageDropdownWidget },
    location: {
      ...locationUISchema,
    },
    userIds: {
      items: { "ui:widget": AutocompleteWidget },
    },
    expirationDate: { "ui:widget": "datetime" },
    description: { "ui:widget": "textarea" },
  } as const,

  ObjectFieldTemplate: CustomObjectFieldTabs,

  async imageProcessor(
    formData: { iconUrl?: unknown; image?: unknown },
    handleUpload: (
      id: string,
      fieldName: string,
      image: string,
      imageInfo: any,
      imagePropName: any,
    ) => Promise<any>,
    id: string,
  ) {
    const result = { ...formData }

    try {
      // Show loading state for image uploads
      if (result.iconUrl) {
        console.log("Uploading icon for featured business:", id)
        result.iconUrl = await handleUpload(
          id,
          "iconUrl",
          result.iconUrl as string,
          undefined,
          "iconUrl",
        )
      }
      if (result.image) {
        console.log("Uploading image for featured business:", id)
        result.image = await handleUpload(
          id,
          "image",
          result.image as string,
          undefined,
          "image",
        )
      }
      console.log("Image processing completed for featured business:", id)
    } catch (error) {
      console.error("Error processing images for featured business:", error)
      throw new Error("Failed to upload images. Please try again.")
    }

    return result
  },

  // Registrar widgets personalizados
  widgets: {
    // ...getMultiLangStringFor_widgets(),
    typeSelect: TypeSelectWidget,
    storesMultiSelect: StoresMultiSelectWidget,
    businessSelect: BusinessSelectWidget,
    serviceSelect: ServiceSelectWidget,
    sponsorSelect: SponsorSelectWidget,
    externalBusinessUrl: ExternalUrlWidget,
    LocationSelector,
    SelectWidget,
    image: ImageWidget,
    // Asegurar que "datetime" del uiSchema esté mapeado
    datetime: DateTimeWidget,
  },
  otherProps: {
    dependencies: {
      type: {
        oneOf: [
          // Business branch
          {
            properties: {
              type: { const: "Business" },
              externalBusinessUrl: {
                type: "string",
                format: "uri",
                title: "External Business URL",
              },
            },
            // required: ["externalBusinessUrl"],
          },

          // Service branch
          {
            properties: {
              type: { const: "Service" },
              businessId: { type: "string", title: "Business" },
              serviceId: { type: "string", title: "Service" },
            },
            required: ["businessId", "serviceId"],
          },

          // Stores branch
          {
            properties: {
              type: { const: "Stores" },
              businessIds: {
                type: "array",
                title: "Stores",
                items: { type: "string" },
                minItems: 1,
                uniqueItems: true,
              },
            },
            required: ["businessIds"],
          },

          // Sponsor branch
          {
            properties: {
              type: { const: "Sponsor" },
              sponsorId: { type: "string", title: "Sponsor" },
            },
            required: ["sponsorId"],
          },
        ],
      },
    },

    // allOf: [
    //   // Business branch
    //   {
    //     if: { properties: { type: { const: "Business" } } },
    //     then: {
    //       properties: {
    //         externalBusinessUrl: { type: "string", format: "uri", title: "External Business URL" },
    //       },
    //       required: ["externalBusinessUrl"],
    //     },
    //   },

    //   // Service branch
    //   {
    //     if: { properties: { type: { const: "Service" } } },
    //     then: {
    //       properties: {
    //         businessId: { type: "string", title: "Business" },
    //         serviceId: { type: "string", title: "Service" },
    //       },
    //       required: ["businessId", "serviceId"],
    //     },
    //   },

    //   // Stores branch
    //   {
    //     if: { properties: { type: { const: "Stores" } } },
    //     then: {
    //       properties: {
    //         businessIds: {
    //           type: "array",
    //           title: "Stores",
    //           items: { type: "string" },
    //           minItems: 1,
    //           uniqueItems: true,
    //         },
    //       },
    //       required: ["businessIds"],
    //     },
    //   },
    // ],
  },
}

/** =========================
 *  Component
 *  ========================= */
const FeaturedBusinesses: React.FC = () => {
  // Main component that renders the featured businesses management interface
  // Uses DataProvider and ListComponent for CRUD operations with loading states
  return (
    <div>
      <DataProvider {...featureBusinessConfigData}>
        <ListComponent />
      </DataProvider>
    </div>
  )
}

export default FeaturedBusinesses
